import React, { useCallback, useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ExternalLink, GripVertical, LoaderCircle, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { DeleteConfirmationButton } from "../common/ConfirmationDialog";
import { InlineFormFileUpload } from "../file-upload/InlineFormFileUpload";
import apiClient from "../../lib/utils/network-client";
import useDragAutoScroll from "../../hooks/useDragAutoScroll";
import useToast from "../../hooks/useToast";

const EMPTY_DOCUMENT = {
  clause_code: "",
  title: "",
  url: "",
  content_type: "pdf",
  content: "",
  pdf: null,
  remove_pdf: false,
  status: "published",
};

const PDF_MAX_SIZE = 25 * 1024 * 1024;
const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000/";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000/api";
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "").replace(/\/+$/, "");
const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const getContentType = (document) => {
  if (document?.content_type) return document.content_type;
  if (document?.link_type === "pdf") return "pdf";
  if (document?.link_type === "external") return "external";
  return "dynamic";
};

const getPublicUrl = (value) => {
  if (!value || /^https?:\/\//i.test(value)) return value || "";
  if (value.startsWith("/uploads/")) return `${API_ORIGIN}${value}`;
  return `${WEBSITE_URL.replace(/\/+$/, "")}/${value.replace(/^\/+/, "")}`;
};

const MsrDocumentsPanel = ({ canCreate, canEdit, canDelete }) => {
  const { showErrorToast, showSuccessToast } = useToast();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_DOCUMENT);
  const [isSaving, setIsSaving] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const canReorder = canEdit && !isLoading && !isReordering && documents.length > 1;
  const { handleDragAutoScroll, stopDragAutoScroll } = useDragAutoScroll(
    Boolean(draggedId),
    canReorder,
  );

  const fetchDocuments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/msr/admin/documents");
      setDocuments(response.data?.data?.documents || []);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load MSR content");
    } finally {
      setIsLoading(false);
    }
  }, [showErrorToast]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const openDialog = (document = null) => {
    setEditing(document);
    setForm(document ? {
      clause_code: document.clause_code || "",
      title: document.title || "",
      url: document.url || "",
      content_type: getContentType(document),
      content: document.content || "",
      pdf: null,
      remove_pdf: false,
      status: document.status || "published",
    } : EMPTY_DOCUMENT);
    setDialogOpen(true);
  };

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("clause_code", form.clause_code);
      payload.append("title", form.title);
      payload.append("content_type", form.content_type);
      payload.append("url", form.url || "");
      payload.append("content", form.content || "");
      payload.append("status", form.status);
      payload.append("remove_pdf", String(form.remove_pdf));
      if (form.pdf) payload.append("pdf", form.pdf);

      if (editing) await apiClient.put(`/msr/documents/${editing.id}`, payload);
      else await apiClient.post("/msr/documents", payload);
      showSuccessToast(`MSR content ${editing ? "updated" : "created"} successfully`);
      setDialogOpen(false);
      fetchDocuments();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save MSR content");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (document) => {
    try {
      await apiClient.delete(`/msr/documents/${document.id}`);
      showSuccessToast("MSR content deleted successfully");
      fetchDocuments();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete MSR content");
    }
  };

  const handleDrop = async (event, targetId) => {
    event.preventDefault();
    stopDragAutoScroll();
    if (!canReorder || !draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDropTargetId(null);
      return;
    }

    const sourceIndex = documents.findIndex((item) => item.id === draggedId);
    const targetIndex = documents.findIndex((item) => item.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      setDraggedId(null);
      setDropTargetId(null);
      return;
    }

    const previousDocuments = [...documents];
    const reorderedDocuments = [...documents];
    const [movedDocument] = reorderedDocuments.splice(sourceIndex, 1);
    reorderedDocuments.splice(targetIndex, 0, movedDocument);
    const normalizedDocuments = reorderedDocuments.map((item, index) => ({
      ...item,
      sort_order: index + 1,
    }));

    setDocuments(normalizedDocuments);
    setDraggedId(null);
    setDropTargetId(null);
    setIsReordering(true);
    try {
      const response = await apiClient.put("/msr/documents/reorder", {
        orderedItems: normalizedDocuments.map((item) => ({
          id: item.id,
          sort_order: item.sort_order,
        })),
      });
      setDocuments(response.data?.data?.documents || normalizedDocuments);
      showSuccessToast("MSR clause order updated successfully");
    } catch (err) {
      setDocuments(previousDocuments);
      showErrorToast(err.response?.data?.message || "Unable to update MSR clause order");
    } finally {
      setIsReordering(false);
    }
  };

  return (
    <div className="space-y-4" onDragOver={handleDragAutoScroll}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
          {isReordering ? <LoaderCircle className="h-4 w-4 animate-spin text-primary" /> : <GripVertical className="h-4 w-4" />}
          {isReordering ? "Saving order..." : "Drag the handle to reorder; hold near an edge to auto-scroll"}
        </p>
        <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={fetchDocuments}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
        {canCreate && (
          <Button type="button" onClick={() => openDialog()}>
            <Plus className="mr-2 h-4 w-4" /> Add MSR Content
          </Button>
        )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-16 text-center text-sm font-medium text-slate-500">Loading MSR content...</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {documents.map((document) => {
              const contentType = getContentType(document);
              const sourceUrl = contentType === "pdf" ? document.pdf_url || document.url : document.url;
              return (
                <div
                  key={document.id}
                  onDragOver={(event) => {
                    if (!canReorder || !draggedId) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    if (draggedId !== document.id) setDropTargetId(document.id);
                  }}
                  onDrop={(event) => handleDrop(event, document.id)}
                  className={`flex flex-col gap-4 p-5 transition lg:flex-row lg:items-center ${
                    draggedId === document.id
                      ? "bg-blue-50/70 opacity-50"
                      : dropTargetId === document.id
                        ? "bg-primary/5 ring-2 ring-inset ring-primary/20"
                        : "hover:bg-slate-50/60"
                  }`}
                >
                  <button
                    type="button"
                    draggable={canReorder}
                    disabled={!canReorder}
                    aria-label={`Drag to reorder clause ${document.clause_code}`}
                    title="Drag to reorder"
                    onDragStart={(event) => {
                      if (!canReorder) return;
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", document.id);
                      setDraggedId(document.id);
                    }}
                    onDragEnd={() => {
                      stopDragAutoScroll();
                      setDraggedId(null);
                      setDropTargetId(null);
                    }}
                    className="inline-flex h-10 w-10 shrink-0 cursor-grab items-center justify-center self-start rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary active:cursor-grabbing disabled:cursor-wait disabled:opacity-50 lg:self-auto"
                  >
                    <GripVertical className="h-5 w-5" />
                  </button>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 font-black text-primary">
                    {document.clause_code}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold leading-6 text-slate-900">{document.title}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <Badge className="border-blue-100 bg-blue-50 text-blue-700">{contentType === "content" ? "written content" : contentType}</Badge>
                      <Badge className={document.status === "published" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}>{document.status}</Badge>
                      {contentType === "content" ? (
                        <span>Content is stored in the website</span>
                      ) : sourceUrl ? (
                        <a href={getPublicUrl(sourceUrl)} target="_blank" rel="noreferrer" className="inline-flex min-w-0 items-center gap-1 text-primary hover:underline">
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          <span className="max-w-sm truncate">{sourceUrl}</span>
                        </a>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {canEdit && <Button type="button" variant="outline" onClick={() => openDialog(document)}><Pencil className="mr-2 h-4 w-4" /> Edit</Button>}
                    {canDelete && (
                      <DeleteConfirmationButton
                        title="Delete MSR content?"
                        description={`This will remove “${document.title}” from the public MSR index.`}
                        onConfirm={() => handleDelete(document)}
                      >
                        <Button type="button" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                      </DeleteConfirmationButton>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Add"} MSR content</DialogTitle>
            <DialogDescription>Upload a PDF or write content that visitors can read directly on the MSR page.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-2 sm:col-span-2"><span className="text-sm font-semibold text-slate-700">Clause Code</span><Input required value={form.clause_code} onChange={(event) => updateField("clause_code", event.target.value)} /></label>
              <label className="space-y-2 sm:col-span-2"><span className="text-sm font-semibold text-slate-700">Title</span><Textarea required value={form.title} onChange={(event) => updateField("title", event.target.value)} /></label>
              <label className="space-y-2"><span className="text-sm font-semibold text-slate-700">Content Type</span><Select value={form.content_type} onValueChange={(value) => updateField("content_type", value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pdf">Upload PDF</SelectItem><SelectItem value="content">Write content</SelectItem><SelectItem value="dynamic">Dynamic data page</SelectItem><SelectItem value="external">External link</SelectItem></SelectContent></Select></label>
              <label className="space-y-2"><span className="text-sm font-semibold text-slate-700">Status</span><Select value={form.status} onValueChange={(value) => updateField("status", value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent></Select></label>
            </div>

            {form.content_type === "pdf" && (
              <InlineFormFileUpload
                label="PDF Document"
                value={form.pdf}
                onChange={(file) => setForm((current) => ({ ...current, pdf: file }))}
                existingFiles={editing && !form.remove_pdf && (editing.pdf_url || (getContentType(editing) === "pdf" && editing.url)) ? [{ id: editing.id, name: `${editing.clause_code || "MSR"}.pdf`, url: getPublicUrl(editing.pdf_url || editing.url), mimeType: "application/pdf" }] : []}
                onRemoveExisting={() => setForm((current) => ({ ...current, remove_pdf: true, pdf: null }))}
                accept={["application/pdf"]}
                maxSizeBytes={PDF_MAX_SIZE}
                required={!editing || form.remove_pdf}
                helperText="PDF only, maximum 25 MB. Replace or remove actions ask for confirmation."
              />
            )}

            {form.content_type === "content" && (
              <div className="space-y-2">
                <span className="text-sm font-semibold text-slate-700">Written Content</span>
                <ReactQuill theme="snow" value={form.content} onChange={(value) => updateField("content", value)} modules={quillModules} placeholder="Write the information visitors should see..." />
              </div>
            )}

            {["dynamic", "external"].includes(form.content_type) && (
              <label className="block space-y-2"><span className="text-sm font-semibold text-slate-700">{form.content_type === "dynamic" ? "Website Route" : "External URL"}</span><Input required value={form.url} onChange={(event) => updateField("url", event.target.value)} placeholder={form.content_type === "dynamic" ? "/b1" : "https://..."} /></label>
            )}

            <DialogFooter className="gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={isSaving}>{isSaving ? "Saving..." : "Save MSR Content"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MsrDocumentsPanel;
